import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, catchError, of } from 'rxjs';
import { 
  ChatMessage, 
  ChatSession, 
  ChatStatus, 
  MessageType, 
  MessageStatus,
  ChatConfiguration,
  TypingIndicator
} from '../interfaces/chat.interface';
import { envPython } from 'src/environments/environments';

// Interface para la respuesta de la API
interface ApiResponse {
  output: string;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private readonly API_URL = envPython.url_ia;

  private currentSession$ = new BehaviorSubject<ChatSession | null>(null);
  private messages$ = new BehaviorSubject<ChatMessage[]>([]);
  private unreadCount$ = new BehaviorSubject<number>(0);
  private typingIndicator$ = new Subject<TypingIndicator>();
  private connectionStatus$ = new BehaviorSubject<boolean>(true);

  // Configuración por defecto
  private readonly defaultConfig: ChatConfiguration = {
    maxMessages: 100,
    autoClose: false,
    autoCloseTime: 30,
    showTypingIndicator: true,
    allowFileUpload: false,
    maxFileSize: 5,
    welcomeMessage: '¡Hola! Soy ClaudIA, tu asistente virtual. ¿En qué puedo ayudarte hoy?'
  };

  private configuration: ChatConfiguration = this.defaultConfig;

  constructor(private http: HttpClient) {
    this.initializeChat();
  }

  // Getters para observables
  get currentSession(): Observable<ChatSession | null> {
    return this.currentSession$.asObservable();
  }

  get messages(): Observable<ChatMessage[]> {
    return this.messages$.asObservable();
  }

  get unreadCount(): Observable<number> {
    return this.unreadCount$.asObservable();
  }

  get typingIndicator(): Observable<TypingIndicator> {
    return this.typingIndicator$.asObservable();
  }

  get connectionStatus(): Observable<boolean> {
    return this.connectionStatus$.asObservable();
  }

  // Inicializar el chat
  private initializeChat(): void {
    this.testConnection();
    this.createNewSession();
  }

  // Probar conexión con la API
  private testConnection(): void {
    const testFormData = new FormData();
    testFormData.append('pregunta', 'test');

    this.http.post<ApiResponse>(this.API_URL, testFormData).subscribe({
      next: (response) => {
        this.connectionStatus$.next(true);
      },
      error: (error) => {
        this.connectionStatus$.next(false);
        console.error('❌ Error conectando con ClaudIA:', error);
        // Reintentar después de 10 segundos
        setTimeout(() => this.testConnection(), 10000);
      }
    });
  }

  // Crear nueva sesión de chat
  createNewSession(): void {
    const newSession: ChatSession = {
      id: this.generateId(),
      userId: 'current-user',
      status: ChatStatus.ACTIVE,
      createdAt: new Date(),
      updatedAt: new Date(),
      messages: [],
      unreadCount: 0
    };

    this.currentSession$.next(newSession);
    this.messages$.next([]);
    this.unreadCount$.next(0);

    // Enviar mensaje de bienvenida después de un breve delay
    if (this.configuration.welcomeMessage) {
      setTimeout(() => {
        this.addSystemMessage(this.configuration.welcomeMessage!);
      }, 1000);
    }
  }

  // Enviar mensaje
  sendMessage(content: string, type: MessageType = MessageType.TEXT): void {
    if (!content.trim()) return;

    const session = this.currentSession$.value;
    if (!session) {
      console.error('No hay sesión activa');
      return;
    }

    // Crear mensaje del usuario
    const userMessage: ChatMessage = {
      id: this.generateId(),
      content: content.trim(),
      timestamp: new Date(),
      sender: {
        id: 'current-user',
        name: 'Usuario',
        type: 'user'
      },
      type,
      status: MessageStatus.SENDING
    };

    // Añadir mensaje del usuario inmediatamente
    const currentMessages = this.messages$.value;
    this.messages$.next([...currentMessages, userMessage]);

    // Marcar como enviado y enviar a la API
    setTimeout(() => {
      this.updateMessageStatus(userMessage.id, MessageStatus.SENT);
      this.sendToAPI(content);
    }, 300);

    // Actualizar sesión
    session.messages = this.messages$.value;
    session.updatedAt = new Date();
    this.currentSession$.next(session);
  }

  // Enviar mensaje a la API
  private sendToAPI(question: string): void {
    if (!this.connectionStatus$.value) {
      this.handleAPIError('No hay conexión con ClaudIA');
      return;
    }

    // Mostrar indicador de escritura
    this.showTypingIndicator();

    // Preparar FormData
    const formData = new FormData();
    formData.append('pregunta', question);

    // Enviar petición
    this.http.post<ApiResponse>(this.API_URL, formData)
      .pipe(
        catchError(error => {
          console.error('Error en la API:', error);
          this.handleAPIError('Error al comunicarse con ClaudIA');
          return of(null);
        })
      )
      .subscribe(response => {
        this.hideTypingIndicator();
        
        if (response && response.output) {
          this.addAgentMessage(response.output);
        } else {
          this.handleAPIError('Respuesta inválida de ClaudIA');
        }
      });
  }

  // Manejar errores de la API
  private handleAPIError(errorMessage: string): void {
    this.hideTypingIndicator();
    this.connectionStatus$.next(false);
    
    const errorResponse = `Lo siento, ${errorMessage}. Por favor intenta nuevamente en unos momentos.`;
    this.addAgentMessage(errorResponse);
    
    // Reintentar conexión después de 10 segundos
    setTimeout(() => this.testConnection(), 10000);
  }

  // Añadir mensaje del agente
  private addAgentMessage(content: string): void {
    const agentMessage: ChatMessage = {
      id: this.generateId(),
      content,
      timestamp: new Date(),
      sender: {
        id: 'claudia-ai',
        name: 'ClaudIA',
        type: 'agent'
      },
      type: MessageType.TEXT,
      status: MessageStatus.DELIVERED
    };

    const currentMessages = this.messages$.value;
    this.messages$.next([...currentMessages, agentMessage]);

    // Incrementar contador de no leídos si el chat no está visible
    this.incrementUnreadCount();
  }

  // Mostrar indicador de escritura
  private showTypingIndicator(): void {
    if (this.configuration.showTypingIndicator) {
      this.typingIndicator$.next({
        isTyping: true,
        sender: {
          id: 'claudia-ai',
          name: 'ClaudIA',
          type: 'agent'
        },
        timestamp: new Date()
      });
    }
  }

  // Ocultar indicador de escritura
  private hideTypingIndicator(): void {
    this.typingIndicator$.next({
      isTyping: false,
      sender: {
        id: 'claudia-ai',
        name: 'ClaudIA',
        type: 'agent'
      },
      timestamp: new Date()
    });
  }

  // Añadir mensaje del sistema
  private addSystemMessage(content: string): void {
    const systemMessage: ChatMessage = {
      id: this.generateId(),
      content,
      timestamp: new Date(),
      sender: {
        id: 'system',
        name: 'Sistema',
        type: 'system'
      },
      type: MessageType.SYSTEM,
      status: MessageStatus.DELIVERED
    };

    const currentMessages = this.messages$.value;
    this.messages$.next([...currentMessages, systemMessage]);
  }

  // Actualizar estado del mensaje
  private updateMessageStatus(messageId: string, status: MessageStatus): void {
    const currentMessages = this.messages$.value;
    const updatedMessages = currentMessages.map(msg => 
      msg.id === messageId ? { ...msg, status } : msg
    );
    this.messages$.next(updatedMessages);
  }

  // Incrementar contador de mensajes no leídos
  private incrementUnreadCount(): void {
    const current = this.unreadCount$.value;
    this.unreadCount$.next(current + 1);
  }

  // Marcar mensajes como leídos
  markAsRead(): void {
    this.unreadCount$.next(0);
    
    // Actualizar estado de los mensajes a leído
    const currentMessages = this.messages$.value;
    const updatedMessages = currentMessages.map(msg => 
      msg.sender.type !== 'user' && msg.status !== MessageStatus.READ 
        ? { ...msg, status: MessageStatus.READ } 
        : msg
    );
    this.messages$.next(updatedMessages);
  }

  // Cerrar sesión de chat
  closeSession(): void {
    const session = this.currentSession$.value;
    if (session) {
      session.status = ChatStatus.CLOSED;
      session.updatedAt = new Date();
      this.currentSession$.next(session);
    }
  }

  // Obtener configuración
  getConfiguration(): ChatConfiguration {
    return this.configuration;
  }

  // Actualizar configuración
  updateConfiguration(config: Partial<ChatConfiguration>): void {
    this.configuration = { ...this.configuration, ...config };
  }

  // Reintentar conexión manualmente
  retryConnection(): void {
    this.testConnection();
  }

  // Limpiar sesión y reiniciar
  resetChat(): void {
    this.messages$.next([]);
    this.unreadCount$.next(0);
    this.createNewSession();
  }

  // Utilidad para generar IDs únicos
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
  }
}